import './applicantAdditionalIncomePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import { NO, YES, YesOrNo } from '../../../store/applicantStore/types/YesOrNo';
import useApplicantStore from '../../../store/applicantStore/useApplicantStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    hasAdditionalIncome,
    isSyncingIncome,
    syncingIncomeFailedMessage,
    setHasAdditionalIncome,
    syncIncome,
  } = useApplicantStore();

  const {
    formState: { isDirty },
    handleSubmit,
    register,
    watch,
  } = useForm({
    defaultValues: {
      hasAdditionalIncomeValue: hasAdditionalIncome,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<YesOrNo>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const hasAdditionalIncomeValue = event.target.value;

    setIsInitialSelection(
      isEmpty(hasAdditionalIncome) && isValidClickEvent(event),
    );

    setHasAdditionalIncome(hasAdditionalIncomeValue);
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { hasAdditionalIncomeValue } = values;

    if (!hasAdditionalIncomeValue) {
      return;
    }

    // the form is dirty and the user selected "No" for additional income,
    // we should sync to clear out any existing values.
    if (isDirty && hasAdditionalIncomeValue === NO) {
      const synced = await syncIncome();
      if (synced) {
        navigate(next());
      }

      return;
    }

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <Box className="applicant-additional-income-page" sx={theme.pageLayout}>
        <header
          data-testid={locator.pages.applicantAdditionalIncome.header.root}
        >
          <button
            aria-label="Previous step"
            data-testid={
              locator.pages.applicantAdditionalIncome.header.previousStepButton
            }
            type="button"
            onClick={previousStep}
          >
            <KeyboardBackspaceRoundedIcon />
          </button>
          <button
            aria-label="Toggle drawer"
            data-testid={
              locator.pages.applicantAdditionalIncome.header.toggleDrawerButton
            }
            type="button"
            onClick={(): void => {
              setDrawerOpen(true);
            }}
          >
            <MenuRoundedIcon />
          </button>
        </header>

        <div className="progress-container" role="complementary">
          <LinearProgress
            aria-label="Application completed percentage"
            data-testid={locator.pages.applicantAdditionalIncome.progress.root}
            value={useWorkflowStore.getState().computed.progress}
            variant="determinate"
          />
        </div>

        <main>
          <form
            data-testid={locator.pages.applicantAdditionalIncome.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applicantAdditionalIncome.form.h1}>
                {t('ApplicantAdditionalIncomePage.title')}
              </h1>
              <h2 data-testid={locator.pages.applicantAdditionalIncome.form.h2}>
                {t('ApplicantAdditionalIncomePage.subtitle')}
              </h2>

              {!isEmpty(syncingIncomeFailedMessage) && (
                <div style={{ marginBottom: '2rem' }}>
                  <Alert
                    data-testid={
                      locator.pages.applicantAdditionalIncome.alert.error
                    }
                    icon={false}
                    severity="error"
                  >
                    {syncingIncomeFailedMessage}
                  </Alert>
                </div>
              )}

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="hasAdditionalIncomeValue"
                  options={[
                    {
                      label: t('ApplicantAdditionalIncomePage.yes'),
                      value: YES,
                      testId:
                        locator.pages.applicantAdditionalIncome.form
                          .radioOption1,
                    },
                    {
                      label: t('ApplicantAdditionalIncomePage.no'),
                      value: NO,
                      testId:
                        locator.pages.applicantAdditionalIncome.form
                          .radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applicantAdditionalIncome.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  color="primary"
                  data-testid={
                    locator.pages.applicantAdditionalIncome.form.submitButton
                  }
                  disabled={isEmpty(hasAdditionalIncome)}
                  loading={isSyncingIncome}
                  startIcon={<></>}
                  type="submit"
                  variant="contained"
                  fullWidth
                >
                  {t('ApplicantAdditionalIncomePage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applicantAdditionalIncome.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </Box>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplicantAdditionalIncomePage';
